/**
 * 管理端会员页共享类型。
 * 字段命名对齐后端响应（经 ResponseInterceptor + admin client 双重 camelCase 转换）。
 */

/** 会员等级行（GET /admin/membership/levels） */
export interface LevelRow {
  id: string;
  code: string;
  name: string;
  description: string | null;
  isDefault: boolean;
  sortOrder: number;
  createdAt?: string;
  updatedAt?: string;
}

/** 功能权限行（GET /admin/membership/features） */
export interface FeatureRow {
  id: string;
  key: string;
  name: string;
  description: string | null;
  groupName: string | null;
  isSystem: boolean;
  sortOrder: number;
}

/** 对比矩阵中的等级摘要 */
export interface CompareLevel {
  code: string;
  name: string;
  description: string | null;
  isDefault: boolean;
  sortOrder: number;
}

/** 对比矩阵中的功能行（access 为各等级开关） */
export interface CompareFeature {
  key: string;
  name: string;
  groupName: string | null;
  description: string | null;
  access: Array<{ levelCode: string; allowed: boolean }>;
}

/** 对比矩阵响应（GET /admin/membership/level-features/compare） */
export interface CompareResponse {
  levels: CompareLevel[];
  features: CompareFeature[];
}

/**
 * 会员套餐行（GET /admin/membership/plans），与后端 AdminPlanRow 逐字对应：
 * 用户端套餐形状（GET /payment/plans）加上管理端独有的 id / isActive / sortOrder。
 * 金额已由后端 Number() 归一，与用户端同为 number，不再是 Decimal 字符串。
 */
export interface PlanRow {
  id: string;
  planCode: string;
  levelCode: string;
  name: string;
  /** 值域与后端 CreatePlanDto 的 @IsIn 一致（多加 `| string` 会让联合塌缩成 string） */
  billingCycle: 'month' | 'quarter' | 'year';
  durationMonths: number;
  price: number;
  /** 续费价，null 表示与首购价一致 */
  renewPrice: number | null;
  autoRenewSupported: boolean;
  isActive: boolean;
  sortOrder: number;
}

/** 订阅用户行（GET /admin/membership/subscriptions 的 items） */
export interface SubscriptionRow {
  id: string;
  username: string | null;
  fullName: string;
  levelCode: string;
  levelName: string;
  expireAt: string | null;
  autoRenew: boolean;
  status: string | null;
}

/** 订阅列表响应 */
export interface SubscriptionListResponse {
  items: SubscriptionRow[];
  total: number;
  page: number;
  pageSize: number;
}

/** 计费周期展示映射 */
export const BILLING_CYCLE_LABEL: Record<string, string> = {
  month: '按月',
  quarter: '按季度',
  year: '按年',
};

/**
 * 会员等级 code → AntD Tag 配色。
 * 等级由后台增删（GET /admin/membership/levels），未收录的 code 一律回落 'blue'，
 * 因此这里只登记种子等级的既定配色，不要为「将来可能新增的等级」预留条目。
 */
export const LEVEL_COLOR: Record<string, string> = {
  free: 'default',
  premium: 'gold',
};
